import { useState } from "react";
import { toast } from "sonner";
import api, { apiErr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Zap, Check, Loader2, LogOut } from "lucide-react";

const FEATURES = [
  "Facturation & devis illimités",
  "Gestion des stocks et alertes",
  "CRM clients & prospects",
  "Ressources humaines & congés",
  "Paiement des factures en ligne",
  "Relances automatiques & rapports",
  "Utilisateurs illimités dans votre équipe",
];

export default function Billing() {
  const { user, logout } = useAuth();
  const [loading, setLoading] = useState(false);
  const company = user?.company;
  const isOwner = user?.role === "owner";
  const trialing = company?.subscription_status === "trialing";
  const daysLeft = company?.trial_days_left;

  const subscribe = async () => {
    setLoading(true);
    try {
      const { data } = await api.post("/billing/checkout", { origin_url: window.location.origin });
      window.location.href = data.checkout_url;
    } catch (err) {
      toast.error(apiErr(err.response?.data?.detail));
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-background text-foreground grid-noise">
      <div className="mx-auto flex max-w-2xl flex-col items-center px-6 py-16">
        <div className="mb-8 flex items-center gap-2">
          <div className="flex h-9 w-9 items-center justify-center bg-primary text-primary-foreground">
            <Zap size={20} strokeWidth={2.5} />
          </div>
          <span className="font-display text-2xl font-bold">GestionPro</span>
        </div>

        <div className="w-full border border-border bg-card p-8 animate-fade-up">
          {company?.subscription_status === "active" ? (
            <div className="text-center">
              <h1 className="font-display text-3xl font-bold">Abonnement actif</h1>
              <p className="mt-2 text-muted-foreground">Votre accès est actif. Merci !</p>
              <a href="/" className="mt-6 inline-block bg-primary px-6 py-3 text-sm font-medium text-primary-foreground hover:bg-primary/90">Accéder à l'application</a>
            </div>
          ) : (
            <>
              <div className="mb-6 text-center">
                <h1 className="font-display text-3xl font-bold" data-testid="billing-title">
                  {trialing && company?.entitled
                    ? "Passez à l'abonnement complet"
                    : trialing
                    ? "Votre essai est terminé"
                    : "Abonnez-vous pour continuer"}
                </h1>
                <p className="mt-2 text-sm text-muted-foreground">
                  {isOwner
                    ? (trialing && company?.entitled
                        ? "Abonnez-vous dès maintenant — vos jours d'essai restants sont déduits, aucun double paiement."
                        : "Réactivez l'accès de toute votre entreprise en un instant.")
                    : "L'accès de votre entreprise est suspendu. Contactez votre responsable pour réactiver l'abonnement."}
                </p>
              </div>

              <div className="mb-6 border border-border bg-background p-6 text-center">
                <div className="font-display text-5xl font-bold">
                  100€<span className="text-lg font-normal text-muted-foreground">/mois</span>
                </div>
                <p className="mt-1 text-xs uppercase tracking-wide text-muted-foreground">TVA incluse · sans engagement</p>
              </div>

              <ul className="mb-8 space-y-2">
                {FEATURES.map((f) => (
                  <li key={f} className="flex items-center gap-2 text-sm">
                    <Check size={16} className="text-primary" /> {f}
                  </li>
                ))}
              </ul>

              {isOwner ? (
                <button
                  onClick={subscribe}
                  disabled={loading}
                  data-testid="subscribe-btn"
                  className="flex w-full items-center justify-center gap-2 bg-primary px-4 py-3 text-sm font-semibold text-primary-foreground transition-colors hover:bg-primary/90 disabled:opacity-60"
                >
                  {loading && <Loader2 size={16} className="animate-spin" />}
                  S'abonner maintenant
                </button>
              ) : (
                <div className="border border-border bg-background p-4 text-center text-sm text-muted-foreground" data-testid="employee-blocked">
                  Seul le responsable de l'entreprise peut souscrire l'abonnement.
                </div>
              )}
            </>
          )}

          <button onClick={logout} className="mt-6 flex w-full items-center justify-center gap-2 text-xs text-muted-foreground hover:text-foreground">
            <LogOut size={14} /> Se déconnecter
          </button>
        </div>

        {trialing && daysLeft > 0 && (
          <p className="mt-4 text-sm text-primary">Il vous reste {daysLeft} jour(s) d'essai gratuit.</p>
        )}
      </div>
    </div>
  );
}
